import {
  Activity,
  MessageSquare,
  Beaker,
  BookOpen,
  Boxes,
  Building2,
  CalendarCheck,
  Calculator,
  ClipboardList,
  Coins,
  DatabaseBackup,
  FlaskConical,
  LayoutDashboard,
  LayoutList,
  ListChecks,
  FlaskRound,
  History,
  MessageSquareText,
  Microscope,
  Pill,
  NotebookPen,
  PackagePlus,
  Inbox,
  Printer,
  ReceiptText,
  ScrollText,
  Scale,
  ShoppingCart,
  Stethoscope,
  Truck,
  TrendingUp,
  Upload,
  UserPlus,
  Users,
  ChartColumn,
  ShieldCheck,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { Permission } from '#shared/types';
import { routes } from '#client/lib/routes';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ 'aria-hidden'?: boolean }>;
  /** Hidden entirely when the signed-in role lacks this. */
  capability: Permission;
  /** Match only the exact path, for parents of deeper routes. */
  end?: boolean;
}

interface NavGroup {
  /** Omitted for the ungrouped items at the top. */
  label?: string;
  items: NavItem[];
}

/**
 * Navigation grouped by what people do, not by what tables exist.
 *
 * A flat list of entity names — Registration, Patients, Lab tests, Parameters,
 * Panels, Comments, Users — would be the database schema with a font applied. Front-desk work, bench work and
 * configuration are different jobs done by different people, and the grouping
 * says so. It mirrors the structure every comparable diagnostic-lab product
 * uses (registration and billing, then operations, then the catalogue, then
 * administration).
 *
 * Icons come from lucide-react. Every destination has its own glyph so two
 * unrelated destinations never look identical.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    // Ungrouped, above the fold: the two screens every role starts from.
    items: [
      {
        to: routes.dashboard,
        label: 'Home',
        icon: LayoutDashboard,
        capability: 'dashboard.view',
        end: true,
      },
      { to: routes.worklist(), label: 'Worklist', icon: ListChecks, capability: 'worklist.view' },
    ],
  },
  {
    label: 'Front desk',
    items: [
      {
        to: routes.patientNew,
        label: 'Register patient',
        icon: UserPlus,
        capability: 'patient.create',
      },
      {
        to: routes.patients,
        label: 'Patients',
        icon: ClipboardList,
        capability: 'patient.view',
        end: true,
      },
      { to: routes.panels, label: 'Panels', icon: Users, capability: 'panel.view' },
      {
        to: routes.orderRequests,
        label: 'Test requests',
        icon: Inbox,
        capability: 'orderRequest.view',
      },
      {
        to: routes.calculator,
        label: 'Calculator',
        icon: Calculator,
        capability: 'pricing.quote',
      },
      {
        to: routes.dayClose,
        label: 'Day close',
        icon: CalendarCheck,
        capability: 'dayClose.view',
      },
      // Not under Finance: the visit and test counts are a lab-operations fact
      // everyone uses, and the money columns are withheld server-side rather
      // than by hiding the screen.
      {
        to: routes.yearHistory,
        label: 'Year-wise history',
        icon: History,
        capability: 'finance.yearHistory',
      },
    ],
  },
  {
    label: 'Inventory',
    items: [
      {
        to: routes.reagentItems,
        label: 'Reagents',
        icon: FlaskRound,
        capability: 'reagentItem.view',
      },
      {
        to: routes.stockEntry,
        label: 'Enter stock',
        icon: PackagePlus,
        capability: 'lot.create',
      },
      {
        to: routes.reagentRecipes,
        label: 'Recipes',
        icon: Beaker,
        capability: 'recipe.view',
      },
      {
        to: routes.stockLedger,
        label: 'Stock ledger',
        icon: ScrollText,
        capability: 'stock.view',
      },
      {
        to: routes.requisitions,
        label: 'Reagent orders',
        icon: ShoppingCart,
        capability: 'requisition.view',
      },
      { to: routes.suppliers, label: 'Suppliers', icon: Truck, capability: 'supplier.view' },
    ],
  },
  {
    label: 'Catalogue',
    items: [
      { to: routes.labTests, label: 'Lab tests', icon: FlaskConical, capability: 'labTest.view' },
      {
        to: routes.testParameters,
        label: 'Parameters',
        icon: LayoutList,
        capability: 'testParameter.view',
      },
      {
        to: routes.testProcedures,
        label: 'Procedures',
        icon: BookOpen,
        capability: 'procedure.view',
      },
      {
        to: routes.reportRemarks,
        label: 'Remarks',
        icon: NotebookPen,
        capability: 'reportRemark.view',
      },
      {
        to: routes.reportComments,
        label: 'Comments',
        icon: MessageSquareText,
        capability: 'reportComment.view',
      },
      {
        to: routes.antibiotics,
        label: 'Antibiotics',
        icon: Pill,
        capability: 'antibiotic.view',
      },
      {
        to: routes.organisms,
        label: 'Organisms',
        icon: Microscope,
        capability: 'organism.view',
      },
      {
        to: routes.departments,
        label: 'Departments',
        icon: Boxes,
        capability: 'department.view',
      },
      {
        to: routes.consultants,
        label: 'Reporting doctors',
        icon: Stethoscope,
        capability: 'consultant.view',
      },
      { to: routes.rateList, label: 'Rate list', icon: ReceiptText, capability: 'rateList.view' },
    ],
  },
  {
    // Its own group, above Finance, because "how is the lab running" is a
    // question every role has and "what did we earn" is not. One entry: the
    // sections are a nav inside the screen, not seven sidebar rows.
    label: 'Analytics',
    items: [
      { to: routes.analytics, label: 'Analytics', icon: ChartColumn, capability: 'analytics.view' },
    ],
  },
  {
    // Admin-only, all three. Deliberately its own group rather than four more
    // entries under Admin: what the lab earns and what it spends is a distinct
    // job from managing staff and stationery, and burying it makes it unused.
    label: 'Finance',
    items: [
      { to: routes.income, label: 'Income', icon: TrendingUp, capability: 'finance.income' },
      {
        to: routes.profitLoss,
        label: 'Profit and loss',
        icon: Scale,
        capability: 'finance.profitLoss',
      },
      { to: routes.expenses, label: 'Expenses', icon: Coins, capability: 'expense.view' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: routes.users, label: 'Staff', icon: Users, capability: 'staff.view' },
      {
        to: routes.roles,
        label: 'Roles & permissions',
        icon: ShieldCheck,
        capability: 'role.view',
      },
      {
        to: routes.whatsappLog,
        label: 'WhatsApp log',
        icon: MessageSquare,
        capability: 'whatsapp.audit',
      },
      {
        to: routes.printTemplates,
        label: 'Print templates',
        icon: Printer,
        capability: 'printTemplate.manage',
      },
      {
        to: routes.companyDetail,
        label: 'Company details',
        icon: Building2,
        capability: 'labProfile.edit',
      },
      {
        to: routes.imports,
        label: 'Import a sheet',
        icon: Upload,
        capability: 'import.view',
      },
      {
        to: routes.backup,
        label: 'Backup & export',
        icon: DatabaseBackup,
        capability: 'backup.export',
      },
      {
        to: routes.databaseHealth,
        label: 'Database health',
        icon: Activity,
        capability: 'system.health',
      },
    ],
  },
];
